import { Component } from "@theme/component";
import { removeTrapFocus, trapFocus } from "@theme/focus";
import { CartAddEvent } from "@theme/events";
import { fetchConfig, onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.addEventListener("click", this.#onQuickAddClick);
    this.#setupAnimatedElementListeners();
    this.#setupDesktopTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.removeEventListener("click", this.#onQuickAddClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle click on quick-add buttons to add products to cart
   * @param {MouseEvent} event
   */
  #onQuickAddClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target)?.closest(".menu-drawer__quick-add");
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");

    fetch(Theme.routes.cart_add_url, {
      ...fetchConfig("javascript", { body: formData }),
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Error - re-enable button
          button.disabled = false;
          return;
        }

        this.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: "menu-drawer-quick-add",
            itemCount: 1,
          })
        );

        // Brief success state then re-enable
        button.classList.add("menu-drawer__quick-add--added");
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove("menu-drawer__quick-add--added");
        }, 1000);
      })
      .catch(() => {
        button.disabled = false;
      });
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent default browser behavior for details/summary toggle
    // This prevents conflicts between native browser toggle and our JavaScript toggle
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    // If already open, don't do anything
    if (details.hasAttribute("open") && details.classList.contains("menu-open")) {
      return;
    }

    // Set open attribute and aria-expanded
    details.setAttribute("open", "");
    summary.setAttribute("aria-expanded", "true");

    requestAnimationFrame(() => {
      details.classList.add("menu-open");
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]");
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener("animationend", removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup desktop drawer trigger button if this is a desktop drawer
   */
  #setupDesktopTrigger() {
    if (this.dataset.headerDrawerType !== "desktop-drawer") return;

    const trigger = document.querySelector("[data-desktop-drawer-trigger]");
    if (!trigger) return;

    trigger.addEventListener("click", () => {
      this.toggle();
      trigger.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
    });

    // Update trigger aria-expanded when drawer state changes
    const observer = new MutationObserver(() => {
      trigger.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
    });

    observer.observe(this.refs.details, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
